import type { CreationInfo, TaskInfo } from '@/app/types';
import type { DisplayTaskState } from '../TaskCard/types';

const AUDIT_FAILED_ERROR_CODES = new Set(['CreationPolicyViolation', 'failed', 'AuditSubmitIllegal']);

type CompactTaskHistoryStateTitleKey =
  | 'task.status.auditVideoFailed.title'
  | 'task.status.canceled.title'
  | 'task.status.creating.title'
  | 'task.status.empty.title'
  | 'task.status.otherFailed.title'
  | 'task.status.queue.title';

type CompactTaskHistoryStateContentKey =
  | 'task.status.auditVideoFailed.content'
  | 'task.status.canceled.content'
  | 'task.status.empty.content'
  | 'task.status.otherFailed.content';

export interface CompactTaskHistoryStateMessage {
  contentKey?: CompactTaskHistoryStateContentKey;
  icon: 'fail' | 'none';
  loading?: 'processing' | 'queueing';
  titleKey: CompactTaskHistoryStateTitleKey;
}

const COMPACT_TASK_HISTORY_STATE_MESSAGES = {
  auditFailed: {
    contentKey: 'task.status.auditVideoFailed.content',
    icon: 'fail',
    titleKey: 'task.status.auditVideoFailed.title',
  },
  canceled: {
    contentKey: 'task.status.canceled.content',
    icon: 'none',
    titleKey: 'task.status.canceled.title',
  },
  creating: {
    icon: 'none',
    loading: 'processing',
    titleKey: 'task.status.creating.title',
  },
  empty: {
    contentKey: 'task.status.empty.content',
    icon: 'none',
    titleKey: 'task.status.empty.title',
  },
  failed: {
    contentKey: 'task.status.otherFailed.content',
    icon: 'fail',
    titleKey: 'task.status.otherFailed.title',
  },
  queueing: {
    icon: 'none',
    loading: 'queueing',
    titleKey: 'task.status.queue.title',
  },
} satisfies Record<string, CompactTaskHistoryStateMessage>;

const isAuditFailedErrorCode = (errorCode?: string) =>
  errorCode !== undefined && AUDIT_FAILED_ERROR_CODES.has(errorCode);

const getDisplayTaskState = (state: TaskInfo['state'] | DisplayTaskState): DisplayTaskState => {
  if (state === 'created' || state === 'queueing' || state === 'preparation' || state === 'scheduling') {
    return 'queueing';
  }

  if (state === 'unspecified') {
    return 'failed';
  }

  return state;
};

export const getCompactTaskHistoryStateMessage = ({
  creation,
  task,
  taskState = task.state,
}: {
  creation?: CreationInfo;
  task: TaskInfo;
  taskState?: TaskInfo['state'] | DisplayTaskState;
}): CompactTaskHistoryStateMessage | null => {
  const displayTaskState = getDisplayTaskState(taskState);

  if (displayTaskState === 'queueing') {
    return COMPACT_TASK_HISTORY_STATE_MESSAGES.queueing;
  }

  if (displayTaskState === 'processing' || displayTaskState === 'fetchingTaskDetail') {
    return COMPACT_TASK_HISTORY_STATE_MESSAGES.creating;
  }

  if (displayTaskState === 'canceled') {
    return COMPACT_TASK_HISTORY_STATE_MESSAGES.canceled;
  }

  if (displayTaskState === 'failed') {
    return isAuditFailedErrorCode(task.err_code)
      ? COMPACT_TASK_HISTORY_STATE_MESSAGES.auditFailed
      : COMPACT_TASK_HISTORY_STATE_MESSAGES.failed;
  }

  const creationErrorCode = creation?.err_info?.err_code;
  if (creationErrorCode) {
    return isAuditFailedErrorCode(creationErrorCode)
      ? COMPACT_TASK_HISTORY_STATE_MESSAGES.auditFailed
      : COMPACT_TASK_HISTORY_STATE_MESSAGES.failed;
  }

  if (displayTaskState === 'success' && !creation) {
    return COMPACT_TASK_HISTORY_STATE_MESSAGES.empty;
  }

  return null;
};
